import { Card } from 'primereact';
import React, { useRef } from 'react';
import { OverlayPanel } from 'primereact/overlaypanel';

import { storeDashboardAnalysis } from '../service/widget.service';

const CardComponent = ({
  formikData,
  cubeResponse,
  showButtonPreview,
  annotation,
  dashboardDetails,
  dashboardLogDetails,
  item,
  compareToResponse,
}) => {
  const [animatedPercentage, setAnimatedPercentage] = React.useState(0);
  let compareTarget = 0;

  if (compareToResponse?.length > 0 && compareToResponse[0] != null) {
    compareTarget = Object.values(compareToResponse[0])[0];
  }

  const primaryColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--primary-color')
    .trim();
  const color = formikData?.custom_properties?.buttonColor || primaryColor;
  const textColor = formikData?.custom_properties?.buttonTextColor;

  const hexToRgb = inputColor => {
    if (inputColor.startsWith('#')) {
      const hex = inputColor.replace('#', '');
      return [
        parseInt(hex.slice(0, 2), 16),
        parseInt(hex.slice(2, 4), 16),
        parseInt(hex.slice(4, 6), 16),
      ];
    }
    if (/^[0-9a-fA-F]{6}$/.test(inputColor)) {
      return [
        parseInt(inputColor.slice(0, 2), 16),
        parseInt(inputColor.slice(2, 4), 16),
        parseInt(inputColor.slice(4, 6), 16),
      ];
    }
    if (inputColor.startsWith('rgb')) {
      return inputColor.match(/\d+/g).slice(0, 3).map(Number);
    }

    return [130, 214, 231];
  };

  const [animatedValue, setAnimatedValue] = React.useState(0);

  const rgb = hexToRgb(color);

  const parseNumber = valueStr => {
    if (!valueStr) return 0;

    const clean = valueStr
      .toString()
      .replace(/[^\d.,-]/g, '')
      .replace(/,/g, '')
      .replace('.', '.');

    const num = parseFloat(clean);
    return isNaN(num) ? 0 : num;
  };
  const cardAccentColor = formikData?.custom_properties?.widget_accent_color
    ? formikData?.custom_properties.widget_accent_color
    : color;
  const rgbAccent = hexToRgb(cardAccentColor);

  const cardModeStyles = {
    background: `linear-gradient(
                135deg,
                rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 0.9),
                rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 0.05)
              )`,
    border: `1px solid rgba(${rgbAccent[0]}, ${rgbAccent[1]}, ${rgbAccent[2]}, 0.35)`,

    boxShadow: `0 4px 12px rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 0.25)`,
    borderRadius: '10px',

    transition: 'all 0.25s ease-in-out',
  };
  let formatter;
  let value;
  if (cubeResponse?.length) {
    let formatFunc = Object.keys(cubeResponse?.[0])?.[0];
    if (annotation?.[formatFunc]?.meta?.format) {
      formatter = eval(annotation?.[formatFunc]?.meta?.format);
    }
    if (formatter) {
      value = formatter(Object.values(cubeResponse?.[0])?.[0]);
    } else {
      value = Object.values(cubeResponse?.[0])?.[0] || 0;
    }
  }

  const rawValue = parseNumber(value);
  const performanceType = formikData?.custom_properties?.performanceType;

  const targetRawValue =
    performanceType === 'periodComparison'
      ? parseNumber(compareTarget)
      : parseNumber(formikData?.chart_configuration?.measureTarget);
  let formattedTarget = targetRawValue;

  if (formatter) {
    formattedTarget = formatter(targetRawValue);
  }

  const percentageDone =
    targetRawValue > 0 && rawValue > 0
      ? performanceType === 'periodComparison'
        ? ((rawValue - targetRawValue) / rawValue) * 100
        : (rawValue / targetRawValue) * 100
      : 0;

  const animateNumber = (rawNumber, setter) => {
    if (!rawNumber || isNaN(rawNumber)) {
      setter(0);
      return;
    }

    const number = Number(rawNumber);
    const decimalCount = rawNumber.toString().split('.')[1]?.length || 0;

    let start = 0;
    let end = number;
    let startTime = null;
    const duration = 2500;

    const animate = timestamp => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = start + (end - start) * eased;
      const formatted = current.toFixed(decimalCount);

      setter(formatted);

      if (progress < 1) requestAnimationFrame(animate);
    };

    requestAnimationFrame(animate);
  };

  const isIncrease = rawValue >= targetRawValue;
  const performanceIcon = isIncrease ? 'pi pi-arrow-up' : 'pi pi-arrow-down';
  const performanceColor = isIncrease ? 'text-green-700' : 'text-red-700';
  React.useEffect(() => {
    if (value) animateNumber(value, setAnimatedValue);

    if (percentageDone) {
      animateNumber(percentageDone, setAnimatedPercentage);
    }
  }, [value, percentageDone]);

  if (dashboardDetails && dashboardLogDetails) {
    storeDashboardAnalysis({
      ...dashboardLogDetails,
      ...dashboardDetails,
      executionTime: new Date().getTime() - dashboardDetails.start_time,
      end_time: new Date().getTime(),
    });
  }

  const tooltipTargetRef = useRef(null);
  const content = contentType =>
    formikData?.custom_properties?.showPerformance === true ? (
      <>
        {(item?.h || contentType === 'card') && (
          <OverlayPanel
            ref={tooltipTargetRef}
            pt={{
              content: { className: 'p-1' },
              root: { className: 'shadow-3' },
            }}
          >
            <span
              className={`text-sm mt-1 flex gap-1 align-items-center ${performanceColor}`}
            >
              <i className={`pi ${performanceIcon} text-sm`} />
              {Number(animatedPercentage).toFixed(1)}%
              <span className="text-sm text-gray-600">({formattedTarget})</span>
            </span>
          </OverlayPanel>
        )}

        <div
          onMouseEnter={e => tooltipTargetRef.current.show(e)}
          onMouseLeave={e => tooltipTargetRef.current.hide(e)}
          className="w-full font-bold flex flex-column text-900 justify-content-center align-items-center"
          style={{ height: '100%', overflow: 'hidden' }}
        >
          <span className={`text-lg`} style={{ color: textColor }}>
            {value || animatedValue}
          </span>

          {(item?.h > 2 || contentType === 'card') && (
            <span
              className={`text-sm mt-1 flex gap-1 align-items-center ${performanceColor}`}
            >
              <i className={`pi ${performanceIcon} text-sm`} />
              {Number(animatedPercentage).toFixed(1)}%
              <span className="text-sm text-gray-600">({formattedTarget})</span>
            </span>
          )}
        </div>
      </>
    ) : (
      <div
        className="w-full font-bold flex flex-column text-900 justify-content-center align-items-center"
        style={{ height: '100%', overflow: 'hidden' }}
      >
        <span className="text-lg" style={{ color: textColor }}>
          {value || animatedValue}
        </span>
      </div>
    );

  return showButtonPreview ? (
    <div className="w-full flex align-items-center justify-content-center">
      <Card
        className="h-7rem w-20rem"
        style={cardModeStyles}
        pt={{ body: { className: 'p-0' }, content: { className: 'px-2 py-1' } }}
      >
        <span
          className="text-sm font-bold capitalize"
          style={{ color: textColor || 'var(--back-shade-3)' }}
        >
          {formikData.widget_name}
        </span>
        {content('card')}
      </Card>
    </div>
  ) : (
    content('')
  );
};

export default CardComponent;
